// Copyright (c) 2019 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import React, { useState } from "react";
import ReactDOM from "react-dom";
import {
  BrowserRouter as Router,
  Link,
  Redirect,
  Route,
  Switch,
} from "react-router-dom";
import * as serviceWorker from "./serviceWorker";

import { getRoot, useAuthLoader, useAsyncGuard } from "./UiUtil";
import { getApi, User } from "./store/api";
import { LoginPageLink, LoginPagePw } from "./components/LoginPage";
import { PrivacyPolicyPage } from "./components/PrivacyPolicyPage";
import { TermsOfServicePage } from "./components/TermsOfServicePage";
import { HomePage as CoordinatorHomePage } from "./coordinator/HomePage";
import { ActivityListPage as CoordinatorActivityListPage } from "./coordinator/ActivityListPage";
import { ActivityReviewPage as CoordinatorAcitivityReviewPage } from "./coordinator/ActivityReviewPage";
import { HomePage as VolunteerHomePage } from "./volunteer/HomePage";
import { ProfilePage as VolunteerProfilePage } from "./volunteer/ProfilePage";
import { ActivityListPage as VolunteerActivityListPage } from "./volunteer/ActivityListPage";
import { ActivityDetailPage as VolunteerActivityDetailPage } from "./volunteer/ActivityDetailPage";
import { MarkdownPage as VolunteerMarkdownPage } from "./volunteer/MarkdownPage";
import { OrganizationListPage as VolunteerOrganizationListPage } from "./volunteer/OrganizationListPage";
import { OrganizationDetailPage as VolunteerOrganizationDetailPage } from "./volunteer/OrganizationDetailPage";
import { SupervisorListPage as VolunteerSupervisorListPage } from "./volunteer/SupervisorListPage";
import { SupervisorDetailPage as VolunteerSupervisorDetailPage } from "./volunteer/SupervisorDetailPage";
import { ACTIVITY_STATUS, URLS } from "./urls";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { createOuts } from "./util";

const { debug } = createOuts("index", true);

class FourOhFour extends React.Component {
  public render(): React.ReactNode {
    return <div>This page does not exist. TODO 404 error code.</div>;
  }
}

const IndexPage: React.FC<{}> = () => {
  const [redirect, setRedirect] = useState<string | null>(null);

  useAuthLoader(onAuthChange);
  const guard = useAsyncGuard();

  async function onAuthChange(user: User | null) {
    if (user != null && user.email != null) {
      const api = getApi();
      const userInfo = await guard(api.loadUserInfo(user));
      const { allowUnverified, coordinatorRole } = userInfo.data;
      if (user.emailVerified || allowUnverified) {
        // Valid login, so redirect to appropriate home.
        const target = coordinatorRole != null ? URLS.cHome() : URLS.vHome();
        debug(`IndexPage.onAuthChange redirecting to`, target);
        setRedirect(target);
        return;
      }
    }

    // If we didn't detect a valid login above, retry.
    debug("Redirecting back to login page");
    setRedirect(URLS.login());
  }

  return (
    <ErrorBoundary>
      <div className="content">
        {redirect == null ? (
          <Link to={URLS.login()}>Authenticating...</Link>
        ) : (
            <Redirect to={redirect} />
          )}
      </div>
    </ErrorBoundary>
  );
};

function urlParam(name: string, kv: { [key: string]: string }) {
  return `:${name}(${Object.keys(kv)
    .map(x => kv[x])
    .join("|")})`;
}

ReactDOM.render(
  <Router>
    <Switch>
      <Route exact path={URLS.index()} component={IndexPage} />
      <Route exact path={URLS.privacyPolicy()} component={PrivacyPolicyPage} />
      <Route
        exact
        path={URLS.termsOfService()}
        component={TermsOfServicePage}
      />
      <Route exact path={URLS.login()} component={LoginPageLink} />
      <Route exact path={URLS.loginpw()} component={LoginPagePw} />

      {/* Coordinator Routes */}
      <Route exact path={URLS.cHome()} component={CoordinatorHomePage} />
      <Route
        exact
        path={URLS.cActivities(urlParam("status", ACTIVITY_STATUS))}
        component={CoordinatorActivityListPage}
      />
      <Route exact path={URLS.cActivitiesDetail(":uid")} component={CoordinatorAcitivityReviewPage} />

      {/* Volunteer Routes */}
      <Route exact path={URLS.vHome()} component={VolunteerHomePage} />
      <Route exact path={URLS.vProfile()} component={VolunteerProfilePage} />
      <Route
        exact
        path={URLS.vActivities(urlParam("status", ACTIVITY_STATUS))}
        component={VolunteerActivityListPage}
      />
      <Route
        exact
        path={URLS.vActivitiesDetail(":uid")}
        component={VolunteerActivityDetailPage}
      />
      <Route
        exact
        path={URLS.vMarkdown(":uid")}
        component={VolunteerMarkdownPage}
      />
      <Route
        exact
        path={URLS.vOrganizations()}
        component={VolunteerOrganizationListPage}
      />
      <Route
        exact
        path={URLS.vOrganizationsDetail(":uid")}
        component={VolunteerOrganizationDetailPage}
      />
      <Route
        exact
        path={URLS.vSupervisors()}
        component={VolunteerSupervisorListPage}
      />
      <Route
        exact
        path={URLS.vSupervisorsDetail(":uid")}
        component={VolunteerSupervisorDetailPage}
      />

      <Route component={FourOhFour} />
    </Switch>
  </Router>,
  getRoot()
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
